import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { RESET } from "jotai/utils";
import {
  TournamentParticipantList,
  StakeFiatCurrency,
  Tournament,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./GameResultsPage.scss";
import GhostButton from "../../components/dashboard-page/GhostButton/GhostButton";
import FilledButton, {
  ButtonSizes,
} from "../../components/dashboard-page/FilledButton/FilledButton";
import HyperCoin from "../../components/HyperCoin/HyperCoin";
import BulbIcon from "../../components/icons/LightBulbIcon";
import Tip from "../../components/game-play/Tip/Tip";
import InfoIcon2 from "../../components/icons/InfoIcon2";
import useTournamentParticipantsQuery from "../../react-query/queries/use-tournament-participants";
import { duelOpponentAtomWithStorage } from "../../jotai/atoms/tournament";
import ShareGameResultModal from "../../components/game-play/ShareGameResultModal/ShareGameResultModal";
import useStakeOptionsQuery from "../../react-query/queries/use-stake-options";
import useDuelEntryMutation from "../../react-query/mutations/use-duel-entry";
import {
  GameFlowState,
  HttpStatusCodes,
  GameOutcome as Outcome,
  ROUTES,
  TOURNAMENT_TO_GAME_TYPE_MAP,
} from "../../utils/constants";
import Result from "../../components/game-results-page/Result/Result";

import { gameFlowStateAtomWithStorage } from "../../jotai/atoms/games";
import GatheringInfo from "../../components/game-play/GatheringInfo/GatheringInfo";
import UnableToPerformActionModal, {
  GameFailureActions,
} from "../../components/game-play/UnableToPerformActionModal/UnableToPerformActionModal";
import { CustomSpinner } from "../../components/CustomSpinner/CustomSpinner";
import ImageCloseIcon from "../../images/close-icon-white.png";
import ImageX from "../../images/x-outlined-white.svg";
import PrimaryActionButton from "../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import useAuth from "../../hooks/useAuth";
import useAnalytics from "../../hooks/useAnalytics";
import useHprFiatFormatter from "../../hooks/useHprFiatFormatter";

interface GameResultsPageProps {
  tournamentDetails?: Tournament;
}

function GameResultsPage({ tournamentDetails }: GameResultsPageProps) {
  const { t } = useTranslation("games");
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  const [duelOpponent, setDuelOpponent] = useAtom(duelOpponentAtomWithStorage);
  const [gameFlowState] = useAtom(gameFlowStateAtomWithStorage);
  const { userProfile } = useAuth();
  const { sendCompleteGameEvent, sendGoHomeEvent } = useAnalytics();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const {
    data: tournamentParticipants,
    isLoading: isLoadingParticipants,
    isFetching: isFetchingParticipants,
    error: tournamentParticipantsError,
    refetch: refetchTournamentParticipants,
  } = useTournamentParticipantsQuery({
    tournamentId: tournamentId || "",
    enabled: true,
    refetchInterval: 10 * 1000,
    retry: 1,
  });

  const [userOutcome, setUserOutcome] = useState<Outcome>();
  const [opponentOutcome, setOpponentOutcome] = useState<Outcome>();
  const loggedInUsername = userProfile?.username;
  const [isGameResultsModalOpen, setIsGameResultsModalOpen] = useState(false);
  const { data: stakeOptions } = useStakeOptionsQuery({
    currency: userProfile?.currency as StakeFiatCurrency,
  });
  const {
    playBattleGame,
    mutation: { isPending: isBattleGameEntryPending },
  } = useDuelEntryMutation();

  const lastUsedStakeOption = useMemo(
    () =>
      stakeOptions?.records.find(
        (option) => option.stakeAmount === tournamentDetails?.stakeAmount
      ),
    [stakeOptions?.records, tournamentDetails?.stakeAmount]
  );

  const activeGameId = tournamentDetails?.game.id;

  const userInfo = tournamentParticipants?.records.find(
    (participant) => participant.username === loggedInUsername
  );

  const opponentInfo = tournamentParticipants?.records.find(
    (participant) => participant.username !== loggedInUsername
  );

  const evaluateDuelOutcomes = useCallback(
    (participants?: TournamentParticipantList) => {
      const hasBeenMatched = participants?.records.length === 2;

      if (!duelOpponent && !hasBeenMatched) {
        setUserOutcome(Outcome.WAITING_TO_BE_MATCHED);
        setOpponentOutcome(undefined);
        return;
      }
      if (duelOpponent && !hasBeenMatched && !isLoadingParticipants) {
        setUserOutcome(Outcome.OPPONENT_CANCELLED);
        setOpponentOutcome(undefined);
        return;
      }
      if (userInfo?.score != null && opponentInfo?.score == null) {
        setUserOutcome(Outcome.AWAITING_OPPONENT_SCORE);
        setOpponentOutcome(undefined);
        return;
      }
      if (userInfo?.score === undefined || opponentInfo?.score === undefined) {
        return;
      }
      if (userInfo.score > opponentInfo.score) {
        setUserOutcome(Outcome.WIN);
        setOpponentOutcome(Outcome.LOSS);
        return;
      }
      if (userInfo.score < opponentInfo.score) {
        setUserOutcome(Outcome.LOSS);
        setOpponentOutcome(Outcome.WIN);
        return;
      }
      if (userInfo.score === opponentInfo.score) {
        setUserOutcome(Outcome.DRAW);
        setOpponentOutcome(Outcome.DRAW);
        return;
      }
    },
    [duelOpponent, isLoadingParticipants, userInfo, opponentInfo]
  );

  let playerPosition: number | null;
  switch (userOutcome) {
    case Outcome.DRAW:
      playerPosition = 0;
      break;
    case Outcome.WIN:
      playerPosition = 1;
      break;
    case Outcome.LOSS:
      playerPosition = 2;
      break;
    default:
      playerPosition = null;
  }

  let headingText: string;
  switch (userOutcome) {
    case Outcome.WIN:
      headingText = t("game_results_page.you_won");
      break;
    case Outcome.DRAW:
      headingText = t("game_results_page.it_is_a_tie");
      break;
    case Outcome.LOSS:
      headingText = t("game_results_page.second_place");
      break;
    case Outcome.AWAITING_OPPONENT_SCORE:
      headingText = t("game_results_page.waiting_for_your_opponents_score");
      break;
    case Outcome.OPPONENT_CANCELLED:
      headingText = t("game_results_page.you_will_get_a_new_match");
      break;
    case Outcome.WAITING_TO_BE_MATCHED:
      headingText = t("game_results_page.wait_for_a_match");
      break;
    default:
      headingText = "";
  }

  let tipText: string;
  let tipIcon: JSX.Element;
  if (userOutcome === Outcome.AWAITING_OPPONENT_SCORE) {
    tipText = t("game_results_page.we_will_notify_when_score_comes", {
      username: opponentInfo?.username,
    });
    tipIcon = <BulbIcon fill="white" />;
  } else if (
    userOutcome === Outcome.OPPONENT_CANCELLED ||
    userOutcome === Outcome.WAITING_TO_BE_MATCHED
  ) {
    tipText = t("game_results_page.matching_takes_anywhere_key");
    tipIcon = <InfoIcon2 fill="white" width={24} height={24} />;
  } else {
    tipText = "";
    tipIcon = <></>;
  }

  const shareCtaText = useMemo(
    () =>
      userOutcome === Outcome.WIN
        ? t("brag_in_hyper_community")
        : t("post_to_hyper_community"),
    [t, userOutcome]
  );

  const canShowReward =
    userOutcome !== Outcome.AWAITING_OPPONENT_SCORE &&
    userOutcome !== Outcome.OPPONENT_CANCELLED &&
    userOutcome !== Outcome.WAITING_TO_BE_MATCHED;
  const canShareResult =
    userOutcome === Outcome.WIN ||
    userOutcome === Outcome.LOSS ||
    userOutcome === Outcome.DRAW;

  const hasGameFailed =
    (!isFetchingParticipants &&
      userInfo &&
      typeof userInfo.score !== "number") ||
    (gameFlowState === GameFlowState.VIEWING_RESULTS_FOR_FAILED_GAME &&
      !isFetchingParticipants &&
      typeof userInfo?.score !== "number");

  const unableToPerformActionState = useMemo(() => {
    if (gameFlowState === GameFlowState.VIEWING_RESULTS_FOR_FAILED_GAME) {
      const state =
        navigator && !navigator.onLine
          ? GameFailureActions.CONNECTION_LOST
          : GameFailureActions.SUBMIT_SCORES;
      return state;
    }
    return GameFailureActions.LAUNCH_GAME;
  }, [gameFlowState, navigator.onLine]);

  const playAnotherGame = useCallback(() => {
    if (!lastUsedStakeOption?.id || !activeGameId) return;
    setDuelOpponent(RESET);
    playBattleGame(lastUsedStakeOption.id, activeGameId);
  }, [activeGameId, lastUsedStakeOption?.id, playBattleGame, setDuelOpponent]);

  const goToGames = () => {
    if (tournamentDetails) {
      const { stakeAmount, type, game } = tournamentDetails;
      sendGoHomeEvent({
        entry_fee: stakeAmount,
        game_name: game.name,
        game_mode: TOURNAMENT_TO_GAME_TYPE_MAP[type],
        player_position: playerPosition,
      });
    }
    navigate(`${ROUTES.GAMES.ROOT}`);
  };

  const resultEl = useMemo(
    () => (
      <Result
        duelOpponent={duelOpponent}
        headingText={headingText}
        canShowReward={canShowReward}
        opponentInfo={opponentInfo}
        opponentOutcome={opponentOutcome}
        tournamentDetails={tournamentDetails}
        userInfo={userInfo}
        userOutcome={userOutcome}
        hasGameFailed={hasGameFailed}
      />
    ),
    [
      canShowReward,
      duelOpponent,
      hasGameFailed,
      headingText,
      opponentInfo,
      opponentOutcome,
      tournamentDetails,
      userInfo,
      userOutcome,
    ]
  );

  useEffect(() => {
    evaluateDuelOutcomes(tournamentParticipants);
  }, [evaluateDuelOutcomes, tournamentParticipants]);

  useEffect(() => {
    if (
      !tournamentParticipantsError ||
      !(tournamentParticipantsError instanceof AxiosError)
    ) {
      return;
    }

    if (
      tournamentParticipantsError.response?.status === HttpStatusCodes.FORBIDDEN
    ) {
      navigate(`${ROUTES.GAMES.ROOT}`);
    }
  }, [navigate, tournamentParticipantsError]);

  useEffect(() => {
    if (!userOutcome || !tournamentDetails) return;
    const { stakeAmount, type, game, rewardAmount } = tournamentDetails;
    sendCompleteGameEvent({
      entry_fee: stakeAmount,
      game_name: game.name,
      game_mode: TOURNAMENT_TO_GAME_TYPE_MAP[type],
      player_position: playerPosition,
      hpr_reward: rewardAmount,
    });
  }, [userOutcome, tournamentDetails, playerPosition, sendCompleteGameEvent]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const stakeAmount = lastUsedStakeOption?.stakeAmount ?? 0;

  const formattedStakeAmount = isFiatDisplay
    ? formatHprToFiatValue(stakeAmount)
    : stakeAmount;

  return (
    <div
      className={`game-results-page MobileOnly position-relative d-flex flex-column ${
        !isLoadingParticipants ? "justify-content-between" : ""
      }`}
    >
      {isLoadingParticipants || isInitialLoad ? (
        <GatheringInfo
          duelOpponent={duelOpponent}
          tournamentDetails={tournamentDetails}
        />
      ) : (
        <>
          {!hasGameFailed && (
            <GhostButton
              className="rounded-btn"
              style={{ left: 16 }}
              aria-label="close"
              onClick={() =>
                navigate(
                  `${ROUTES.GAMES.ROOT}/${tournamentDetails?.game.slug}${ROUTES.GAMES.PLAY_GAME}`
                )
              }
            >
              <Image src={ImageCloseIcon} alt="" height={14} width={14} />
            </GhostButton>
          )}
          {resultEl}
          {canShareResult && !hasGameFailed && (
            <div className="px-3">
              <PrimaryActionButton
                leftImage={ImageX}
                text={shareCtaText}
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.00) 100%), #333",
                  width: "fit-content",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  height: "38px",
                  borderRadius: "12px",
                }}
                buttonClassName="py-2 px-3"
                textClassName="share-cta-text"
                onClick={() => setIsGameResultsModalOpen(true)}
              />
            </div>
          )}
          {!hasGameFailed && (
            <div className="px-3 pb-4">
              {tipText && (
                <Tip
                  icon={tipIcon}
                  className="mb-3"
                  style={{ textAlign: "left" }}
                >
                  {tipText}
                </Tip>
              )}
              <FilledButton
                className="gap-1"
                fullwidth
                size={ButtonSizes.MD}
                onClick={playAnotherGame}
                disabled={isBattleGameEntryPending}
              >
                {isBattleGameEntryPending ? (
                  <CustomSpinner size="sm" />
                ) : (
                  <>
                    <span>{t("game_results_page.play_another_game")}</span>
                    <span>
                      {!isFiatDisplay && <HyperCoin width={16} />}{" "}
                      {formattedStakeAmount}
                    </span>
                  </>
                )}
              </FilledButton>
              <GhostButton
                className="secondary-btn mx-auto text-white py-3 mt-1"
                onClick={goToGames}
              >
                {t("game_results_page.go_to_games")}
              </GhostButton>
            </div>
          )}
          <ShareGameResultModal
            show={isGameResultsModalOpen}
            onHide={() => setIsGameResultsModalOpen(false)}
            userOutcome={userOutcome}
            playerPosition={playerPosition}
            tournamentDetails={tournamentDetails}
          >
            {resultEl}
          </ShareGameResultModal>
          {hasGameFailed && (
            <UnableToPerformActionModal
              show={hasGameFailed}
              tournament={tournamentDetails}
              action={unableToPerformActionState}
              refetchScores={refetchTournamentParticipants}
            />
          )}
        </>
      )}
    </div>
  );
}

export default GameResultsPage;
